/**
 * @file /purchase route definitions.
 */
import { compose } from 'redux';

import RequireAuth from '../components/routing/RequireAuth';
import RequireAlbumMembership from '../components/routing/RequireAlbumMembership';
import RequireValidAlbum from '../components/routing/RequireValidAlbum';
import RequireValidAlbumFolder from '../components/routing/RequireValidAlbumFolder';
import PurchaseAlbumRedirectContainer from '../containers/purchase/PurchaseAlbumRedirectContainer';
import PurchaseAlbumPhotosRedirectContainer from '../containers/purchase/PurchaseAlbumPhotosRedirectContainer';
import PurchaseAlbumMasterView from '../views/purchase/PurchaseAlbumMasterView';
import PurchaseAlbumView from '../views/purchase/PurchaseAlbumView';
import PurchaseAlbumFacesView from '../views/purchase/PurchaseAlbumFacesView';
import PurchaseAlbumCheckoutView from '../views/purchase/PurchaseAlbumCheckoutView';
import PurchaseAlbumCheckoutSuccessView from '../views/purchase/PurchaseAlbumCheckoutSuccessView';
import PurchasePpdCheckoutView from '../views/purchase/PurchasePpdCheckoutView';
import PurchaseAlbumPhotosCheckoutSuccessView from '../views/purchase/PurchaseAlbumPhotosCheckoutSuccessView';
import PurchaseAlbumFolderMasterView from '../views/purchase/PurchaseAlbumFolderMasterView';
import { AUTH_TYPE } from '../constants/auth';
import AlbumFolderPurchaseSelectIdentitiesView from '../views/purchase/AlbumFolderPurchaseSelectIdentitiesView';
import AlbumFolderPurchaseUniformTagSelectionView from '../views/purchase/AlbumFolderPurchaseUniformTagSelectionView';
import SetAlbumFolder from '../components/routing/discover/SetAlbumFolder';
import AlbumFolderPurchaseSummaryView from '../views/purchase/AlbumFolderPurchaseSummaryView';
import RequireValidAlbumFolderForPurchase from '../components/routing/RequireValidAlbumFolderForPurchase';
import RequireSelectedIdentities from '../components/routing/discover/RequireSelectedIdentities';
import RequirePurchasedAlbumFolder from '../components/routing/discover/RequirePurchasedAlbumFolder';
import PurchasePpdCheckoutSuccessContainer from '../containers/purchase/PurchasePpdCheckoutSuccessContainer';
import AllowIndividualAlbumJoin from '../components/routing/AllowIndividualAlbumJoin';
import SetAlbumFolderJoinPreferences from '../components/routing/discover/SetAlbumFolderJoinPreferences';
import RedirectToJoinTypePreselection from '../components/routing/discover/RedirectToJoinTypePreselection';
import PurchaseAlbumStartContainer from '../containers/purchase/start/PurchaseAlbumStartContainer';
import PurchasePpdPhotoViewerContainer from '../containers/purchase/PurchasePpdPhotoViewerContainer';
import AlbumCustomPrintConfigurationRedirectContainer from '../containers/purchase/custom-prints/AlbumCustomPrintConfigurationRedirectContainer';
import PurchaseAlbumCustomPrintsView from '../views/purchase/PurchaseAlbumCustomPrintsView';
import RequireIdentitiesTags from '../components/routing/discover/RequireIdentitiesTags';
import withCustomGTMDimensions from '../components/routing/discover/withGTMCustomDimensions';
import DiscoverAlbumFolderSuccessView from '../views/discover/DiscoverAlbumFolderSuccessView';
import AlbumFolderPurchaseShutterbugUpsell from '../components/purchase/album-folder-purchase/AlbumFolderPurchaseShutterbugUpsell/AlbumFolderPurchaseShutterbugUpsell';
import withOutdatedAlbumFolderModal from '../components/routing/withOutdatedAlbumFolderModal';
import AlbumFolderAllowsPrePaidItems from '../components/routing/discover/AlbumFolderAllowsPrePaidItems';
import AlbumFolderPurchasePrePurchaseView from '../views/purchase/AlbumFolderPurchasePrePurchaseView';

const routes = [
  {
    path: '/purchase/album/redirect',
    exact: false,
    component: PurchaseAlbumRedirectContainer,
  },
  {
    path: '/purchase/album/photos/redirect',
    exact: false,
    component: PurchaseAlbumPhotosRedirectContainer,
  },
  {
    path: '/purchase/album/:albumId/view/:photoId',
    exact: true,
    component: PurchasePpdPhotoViewerContainer,
  },
  {
    path: '/purchase/album/:albumId',
    exact: false,
    component: RequireAuth(
      compose(
        RequireValidAlbum,
        withOutdatedAlbumFolderModal,
      )(PurchaseAlbumMasterView),
      {
        authType: AUTH_TYPE.PHONE,
      },
    ),
    routes: [
      {
        path: '/purchase/album/:albumId/start',
        exact: true,
        component: PurchaseAlbumStartContainer,
      },
      {
        path: '/purchase/album/:albumId',
        exact: true,
        component: PurchaseAlbumView,
      },
      {
        path: '/purchase/album/:albumId/faces',
        exact: true,
        component: AllowIndividualAlbumJoin(PurchaseAlbumFacesView),
      },
      {
        path: '/purchase/album/:albumId/success',
        exact: true,
        component: PurchasePpdCheckoutSuccessContainer,
      },
      {
        path: '/purchase/album/:albumId/checkout',
        exact: true,
        component: PurchaseAlbumCheckoutView,
      },
      {
        path: '/purchase/album/:albumId/checkout/success',
        exact: true,
        component: PurchaseAlbumCheckoutSuccessView,
      },
      {
        path: '/purchase/album/:albumId/photos/checkout',
        exact: true,
        component: RequireAlbumMembership(PurchasePpdCheckoutView),
      },
      {
        path: '/purchase/album/:albumId/photos/checkout/success',
        exact: true,
        component: PurchaseAlbumPhotosCheckoutSuccessView,
      },
      {
        path: '/purchase/album/:albumId/custom-prints/redirect',
        exact: true,
        component: AlbumCustomPrintConfigurationRedirectContainer,
      },
      {
        path: '/purchase/album/:albumId/custom-prints/:purchasedCustomPrintId',
        exact: true,
        component: PurchaseAlbumCustomPrintsView,
      },
    ],
  },
  {
    path: '/purchase/album-folder/:albumFolderId',
    exact: false,
    component: RequireAuth(
      compose(
        withCustomGTMDimensions,
        RequireValidAlbumFolder,
        SetAlbumFolder,
      )(PurchaseAlbumFolderMasterView),
      { authType: AUTH_TYPE.PHONE },
    ),
    routes: [
      {
        path: '/purchase/album-folder/:albumFolderId/activities',
        exact: true,
        component: compose(RequirePurchasedAlbumFolder)(
          AlbumFolderPurchaseUniformTagSelectionView,
        ),
      },
      {
        path: '/purchase/album-folder/:albumFolderId/purchase-subscription',
        exact: true,
        component: compose(
          RequireValidAlbumFolderForPurchase,
          RedirectToJoinTypePreselection,
          SetAlbumFolderJoinPreferences,
          AlbumFolderAllowsPrePaidItems(),
        )(AlbumFolderPurchaseSelectIdentitiesView),
      },
      {
        path: '/purchase/album-folder/:albumFolderId/pre-purchase',
        exact: true,
        component: compose(
          RequireSelectedIdentities,
          AlbumFolderAllowsPrePaidItems(),
        )(AlbumFolderPurchasePrePurchaseView),
      },
      {
        path: '/purchase/album-folder/:albumFolderId/purchase-summary',
        exact: true,
        component: compose(
          RequireSelectedIdentities,
          RequireValidAlbumFolderForPurchase,
          RequireIdentitiesTags,
          AlbumFolderAllowsPrePaidItems(),
        )(AlbumFolderPurchaseSummaryView),
      },
      {
        path: '/purchase/album-folder/:albumFolderId/success',
        exact: true,
        component: compose(RequireAuth)(DiscoverAlbumFolderSuccessView),
      },
      {
        path: '/purchase/album-folder/:albumFolderId/shutterbug',
        exact: true,
        component: compose(RequireAuth)(AlbumFolderPurchaseShutterbugUpsell),
      },
    ],
  },
];

export default routes;
